import { useMemo } from "react";
import { renderToString } from "react-dom/server";
import { Theme, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toPercent } from "@helpers";
import { OnlyForProChartTooltip } from "@components/Tooltip/OnlyForProChartTooltip";
import { getCommonHighchartsOptions } from "../highcharts/PerformanceCharts/commonChartOptions";
import { HistoricalQuoteTooltip } from "./HistoricalQuoteTooltip";
import { ChartPoint, buildPoints } from "./helpers";
import type { DateValuePair } from "@rentablo/api/apiv1";

export const LOCKED_POINT_CLASS = "historical-quotes-locked";

interface Params {
  historicQuotes: DateValuePair[];
  isUserPro: boolean;
  isUserPlus: boolean;
  height?: number;
}

export const useChartOptions = ({
  historicQuotes,
  isUserPro,
  isUserPlus,
  height,
}: Params): Highcharts.Options => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const points = useMemo(
    () => buildPoints(historicQuotes, isUserPro, isUserPlus),
    [historicQuotes, isUserPro, isUserPlus]
  );

  return useMemo(() => {
    const commonOptions = getCommonHighchartsOptions(theme);
    const axisLabelStyle = {
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.caption.fontSize as string,
      lineHeight: theme.typography.caption.lineHeight as string,
      fontWeight: "500",
      color: theme.palette.text.secondary,
    };

    return {
      ...commonOptions,
      chart: {
        ...commonOptions.chart,
        type: "column",
        ...(height ? { height } : {}),
      },
      title: { text: "" },
      legend: { enabled: false },
      xAxis: {
        type: "linear",
        tickLength: 0,
        tickInterval: 1,
        min: 1,
        max: points.length,
        lineColor: theme.palette.border.secondary,
        labels: {
          formatter: function () {
            const pointIndex = (this.value as number) - 1;
            const yearsAgo = points[pointIndex]?.yearsAgo;
            return t("fundamentals.metrics.historicQuotesPercent.xAxisLabel", {
              value: yearsAgo ?? this.value,
            });
          },
          style: axisLabelStyle,
        },
      },
      yAxis: {
        title: { text: "" },
        tickAmount: 6,
        gridLineColor: theme.palette.border.secondary,
        labels: {
          formatter: function () {
            return `${this.value}%`;
          },
          style: axisLabelStyle,
        },
      },
      tooltip: {
        ...commonOptions.tooltip,
        padding: 0,
        useHTML: true,
        formatter: function () {
          const point = this.point.options as ChartPoint;
          if (point.isLocked) {
            return renderToString(
              <OnlyForProChartTooltip theme={theme} t={t} />
            );
          }
          return renderToString(
            <HistoricalQuoteTooltip
              theme={theme}
              t={t}
              yearsAgo={point.yearsAgo ?? point.x}
              percent={point.percent}
            />
          );
        },
      },
      plotOptions: {
        column: {
          borderWidth: 0,
          borderRadius: 4,
          dataLabels: {
            enabled: !isMobile,
            allowOverlap: true,
            crop: false,
            overflow: "allow",
            style: {
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.overline.fontSize as string,
              lineHeight: theme.typography.overline.lineHeight as string,
              fontWeight: "500",
              color: theme.palette.text.secondary,
              textOutline: "none",
            },
            formatter: function () {
              return toPercent((this.y ?? 0) / 100);
            },
          },
        },
      },
      series: [
        {
          type: "column",
          name: t("fundamentals.metrics.historicQuotesPercent.short"),
          data: points.map((p) => ({
            ...p,
            color: p.isLocked
              ? theme.palette.text.tertiary
              : p.y >= 0
                ? theme.palette.text.positive
                : theme.palette.text.negative,
            ...(p.isLocked && {
              className: LOCKED_POINT_CLASS,
              dataLabels: { className: LOCKED_POINT_CLASS },
            }),
          })),
        },
      ],
    };
  }, [theme, t, points, height, isMobile]);
};
